import React from 'react';
import { Smartphone, Globe, Check } from 'lucide-react';
import Seo from '../components/Seo';
import { IMAGES } from '../mock';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const designedFor = [
  'Tension, worry, and overwhelm',
  'Anxiety about insomnia',
  'Nightmares',
  'Stress and burnout',
  'Trauma responses (as a self-help complement to therapy)',
  'Body tension',
  'Healing past painful stories',
  'Low self-worth',
  'Stage fright & performance pressure',
  'Grief, sadness, and anger',
  'Boundaries and habit-building',
];

const Tools = () => {
  return (
    <div>
      <Seo
        title="Wellness Tools — Bilateral Calm Self-Help"
        description="Explore Bilateral Calm, a self-help tool inspired by EMDR principles for calming, tension relief, and everyday emotional wellbeing — a complement to therapy at The Mood & Sleep Clinic."
        path="/tools"
      />
      {/* Hero with serene beach/ocean background */}
      <section
        className="relative bg-cover bg-center"
        style={{ backgroundImage: `url(/tools-banner.webp)` }}
      >
        <div className="absolute inset-0 bg-[#1f3a5f]/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
          <h1 className="font-heading font-semibold text-white text-4xl sm:text-5xl md:text-6xl tracking-wide drop-shadow fade-up">
            Wellness Tools
          </h1>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-12">
            {/* Left rail */}
            <div className="md:col-span-2">
              <div className="bg-white rounded-2xl border border-border p-8 shadow-sm text-center">
                <img
                  src="/bilateral-calm-logo.webp"
                  alt="Bilateral Calm logo"
                  className="w-28 h-28 mx-auto object-contain"
                  width="112"
                  height="112"
                />
                <h2 className="mt-5 font-heading text-2xl text-navy">Bilateral Calm</h2>
                <p className="mt-1 text-sm font-medium text-[#3d6a9c]">Practices Made for You</p>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  Targeted support for worry, overwhelm, tension, old stories, stage fright, sleeplessness, blues, self-worth, boundaries, grief, anger, performance, and habits.
                </p>
                <div className="mt-6 space-y-3">
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="w-full flex items-center justify-center gap-2 rounded-full bg-navy text-white px-5 py-3 text-sm font-heading font-medium hover:bg-[#2c4d76] transition-colors">
                        <Smartphone size={18} /> Download the App
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xs text-center">
                      <DialogHeader>
                        <DialogTitle className="font-heading text-navy text-xl text-center">Scan to Download</DialogTitle>
                      </DialogHeader>
                      <div className="mt-2 flex flex-col items-center">
                        <div className="rounded-xl border border-border p-3 bg-white">
                          <img
                            src="/bilateral-calm-qr.png"
                            alt="QR code to download the Bilateral Calm app from the Apple App Store"
                            className="w-52 h-52 object-contain"
                            width="208"
                            height="208"
                          />
                        </div>
                        <p className="mt-4 text-sm text-muted-foreground">
                          Point your phone&rsquo;s camera at the code to download Bilateral Calm from the App Store.
                        </p>
                        <a
                          href="https://www.bilateralcalm.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#3d6a9c] hover:text-navy"
                        >
                          <Globe size={15} /> www.bilateralcalm.com
                        </a>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <a href="https://www.bilateralcalm.com" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-full border border-navy/30 text-navy px-5 py-3 text-sm font-heading font-medium hover:bg-secondary transition-colors">
                    <Globe size={18} /> www.bilateralcalm.com
                  </a>
                </div>
              </div>
            </div>

            {/* Right content */}
            <div className="md:col-span-3">
              <h2 className="font-heading text-2xl text-navy">What is Bilateral Calm?</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Bilateral Calm is a self-help tool inspired by EMDR principles — it isn&rsquo;t therapy and doesn&rsquo;t replace working with a licensed mental health provider for trauma or serious mental health conditions.
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                It was made to give your nervous system regulation for calming and tension relief. It can also help address issues bothering you in your day-to-day life. Problems leading to serious mental health challenges should be addressed with a licensed mental health professional.
              </p>

              <h2 className="mt-8 font-heading text-2xl text-navy">What Bilateral Calm is designed for</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Bilateral Calm is made for people needing a nudge in the right direction to improve their lives — at the time and place of your choosing. The practices are organized around everyday emotional experiences most people face. Each one pairs bilateral sound with a structured 9-step guided tactic:
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {designedFor.map((d) => (
                  <li key={d} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check size={17} className="text-[#3d6a9c] mt-0.5 shrink-0" /> {d}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
                A separate &ldquo;Daily Practices&rdquo; audio library offers calming tones for ongoing use, such as deep-calm 432 Hz, gamma 40 Hz associated with memory, ocean waves, and forest tones.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tools;
